import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import { getModuleAdminBySlug, saveModuleContentJson } from "../../api/subjects";

const pretty = (obj) => JSON.stringify(obj, null, 2);

const defaultTemplate = (title) =>
({
    title: title || "Назва модуля",
    questions: [
        {
            id: 1,
            type: "text",
            text: "Вставте HTML або текст завдання сюди",
        },
    ],
});

const ModuleEditorAdminPage = () => {
    const { classId, subjectSlug, authorSlug, moduleSlug } = useParams();
    const nav = useNavigate();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [moduleMeta, setModuleMeta] = useState(null);

    const [contentJsonText, setContentJsonText] = useState("");
    const [titleOverride, setTitleOverride] = useState("");

    const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

    const headerSubtitle = useMemo(() => {
        const parts = [];
        if (classId) parts.push(`Клас: ${classId}`);
        if (subjectSlug) parts.push(`Предмет: ${subjectSlug}`);
        if (authorSlug) parts.push(`Автор: ${authorSlug}`);
        if (moduleSlug) parts.push(`Модуль: ${moduleSlug}`);
        return parts.join(" • ");
    }, [classId, subjectSlug, authorSlug, moduleSlug]);

    const load = useCallback(async () => {
        if (!classId || !subjectSlug || !authorSlug || !moduleSlug) return;

        setLoading(true);
        try {
            const data = await getModuleAdminBySlug(classId, subjectSlug, authorSlug, moduleSlug);
            setModuleMeta(data || null);

            const existing = data?.contentJson;
            if (existing && String(existing).trim()) {
                setContentJsonText(String(existing));
            } else {
                setContentJsonText(pretty(defaultTemplate(data?.title)));
            }

            setTitleOverride(data?.title || "");
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося завантажити модуль";
            setSnack({ open: true, severity: "error", message: String(msg) });
            setModuleMeta(null);
            setContentJsonText(pretty(defaultTemplate("Назва модуля")));
            setTitleOverride("");
        } finally {
            setLoading(false);
        }
    }, [classId, subjectSlug, authorSlug, moduleSlug]);

    useEffect(() => {
        load();
    }, [load]);

    const back = useCallback(() => {
        nav(
            `/manage-portal-9f3a/classes/${classId}/subjects/${subjectSlug}/authors/${authorSlug}/modules`
        );
    }, [nav, classId, subjectSlug, authorSlug]);

    const validateJson = useCallback(() => {
        try {
            const parsed = JSON.parse(contentJsonText);
            if (!parsed || typeof parsed !== "object") return { ok: false, error: "JSON має бути об’єктом" };
            if (!Array.isArray(parsed.questions)) return { ok: false, error: "Поле questions має бути масивом" };
            return { ok: true, parsed };
        } catch (e) {
            return { ok: false, error: "Некоректний JSON" };
        }
    }, [contentJsonText]);

    const onSave = useCallback(async () => {
        const v = validateJson();
        if (!v.ok) {
            setSnack({ open: true, severity: "error", message: v.error });
            return;
        }

        setSaving(true);
        try {
            const normalized = pretty(v.parsed);
            await saveModuleContentJson(classId, subjectSlug, authorSlug, moduleSlug, normalized);

            setContentJsonText(normalized);
            setSnack({ open: true, severity: "success", message: "Збережено" });

            if (titleOverride && titleOverride.trim() && moduleMeta?.title !== titleOverride.trim()) {
                setModuleMeta((m) => (m ? { ...m, title: titleOverride.trim() } : m));
            }
        } catch (e) {
            const status = e?.response?.status;
            const msg = e?.response?.data || e?.message || "Не вдалося зберегти";
            setSnack({
                open: true,
                severity: "error",
                message: status ? `${status}: ${msg}` : String(msg),
            });
        } finally {
            setSaving(false);
        }
    }, [
        validateJson,
        classId,
        subjectSlug,
        authorSlug,
        moduleSlug,
        titleOverride,
        moduleMeta,
    ]);

    const onRebuildTemplate = useCallback(() => {
        const t = String(titleOverride || moduleMeta?.title || "Назва модуля").trim();
        setContentJsonText(pretty(defaultTemplate(t)));
    }, [titleOverride, moduleMeta]);

    return (
        <Container disableGutters maxWidth={false} sx={{ mt: 2 }}>
            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    onClick={back}
                                    startIcon={<ArrowBackIcon />}
                                    variant="text"
                                    sx={{ px: 0, minWidth: 0 }}
                                >
                                    Назад
                                </Button>
                                <Typography variant="h5" fontWeight={700}>
                                    Редактор модуля
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {headerSubtitle}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="outlined"
                                onClick={onRebuildTemplate}
                                disabled={loading || saving}
                            >
                                Шаблон
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                                onClick={onSave}
                                disabled={loading || saving}
                            >
                                Зберегти
                            </Button>
                        </Stack>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                        {loading ? (
                            <Stack direction="row" spacing={2} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Завантаження...
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <TextField
                                    label="Назва (для шаблону)"
                                    value={titleOverride}
                                    onChange={(e) => setTitleOverride(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label="contentJson (JSON модуля)"
                                    value={contentJsonText}
                                    onChange={(e) => setContentJsonText(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={18}
                                    sx={{
                                        "& textarea": {
                                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                            fontSize: 13,
                                        },
                                    }}
                                />
                            </Stack>
                        )}
                    </Box>
                </CardContent>
            </Card>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ModuleEditorAdminPage;
